import {
  FC,
  ReactNode,
  ChangeEvent,
  FocusEvent,
  useEffect,
  useState,
} from 'react';
import classNames from 'classnames';
import NumberFormat from 'react-number-format';
import CalendarIcon from '../icons/CalendarIcon';
import Paragrapgh from '../Paragrapgh';

interface IOwnProps {
  disabled?: boolean;
  type: string;
  className?: string;
  label?: string;
  placeholder?: string;
  value: string;
  unit?: ReactNode;
  readonly?: boolean;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    data?: string,
  ) => void;
  onValChange?: (data: string) => void;
  required?: boolean;
  onValidate?: (e: boolean, data?: string) => void;
  dataType?: string;
  name?: string;
  min?: number;
  max?: number;
  isOptional?: boolean;
  isClear?: boolean;
  isErrorRes?: boolean;
  list?: string;
  rightLabelButton?: ReactNode;
}

const Input: FC<IOwnProps> = ({
  disabled,
  className,
  label,
  type,
  placeholder,
  value,
  unit,
  onChange,
  onValChange,
  required,
  readonly,
  onValidate,
  dataType,
  name,
  min,
  max,
  isOptional,
  isClear,
  isErrorRes,
  list,
  rightLabelButton,
}) => {
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const inputClasses = classNames(className, 'input', {
    'input--disabled': disabled,
    'input--success': isSuccess,
    'input--error': isError,
    'input--marker': unit,
    'input--textarea': type === 'textarea',
    'input--readonly': readonly,
  });

  const handleOnChangeValue = (isValid: boolean) => {
    if (onValidate) {
      onValidate(isValid, dataType);
    }
  };

  const handleOnChangeState = (isValid: boolean) => {
    setIsSuccess(isValid);
    setIsError(!isValid);
    if (onValidate) {
      onValidate(isValid, dataType);
    }
    if (!required && type !== 'number') {
      setIsError(false);
      setIsSuccess(false);
    }
  };

  const checkInput = (inputValue: string, isHandler: boolean) => {
    let isValid: boolean = true;
    if (required) {
      isValid = isValid && inputValue.length >= 1;
    }
    if (type === 'date') {
      isValid = isValid && !isNaN(new Date(inputValue).getTime());
    } else if (type === 'email') {
      isValid =
        isValid && !!inputValue.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i);
    } else if (type === 'password') {
      isValid = isValid && inputValue.length >= 8;
    } else if (type === 'numeric' || type === 'number') {
      if (min) {
        isValid = isValid && Number(inputValue) >= min;
      }
      if (max) {
        isValid = isValid && Number(inputValue) <= max;
      }
    } else if (type === 'time') {
      isValid = isValid && /^([0-9]|1[0-9]|2[0-3]):([0-5]\d)$/.test(inputValue);
    }
    if (isHandler) {
      handleOnChangeState(isValid);
    } else {
      handleOnChangeValue(isValid);
    }
  };

  useEffect(() => {
    checkInput(value, false);
  }, []);

  useEffect(() => {
    if (isErrorRes) {
      setIsError(true);
      if (isSuccess) {
        setIsSuccess(false);
      }
    }
  }, [isErrorRes]);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    checkInput(e.target.value, true);

    if (onChange) {
      onChange(e, dataType);
    }
  };

  const handleOnFocus = (e: FocusEvent<HTMLInputElement>) => {
    checkInput(e.target.value, true);
  };

  useEffect(() => {
    if (isClear) {
      if (isSuccess) {
        setIsSuccess(false);
      }
      if (isError) {
        setIsError(false);
      }
    }
  }, [value]);

  if (type === 'search') {
    return (
      <div className='w-100'>
        <div className={unit ? 'd-flex' : 'pos-relative'}>
          <input
            type={type}
            onChange={handleOnChange}
            disabled={disabled}
            className={inputClasses}
            placeholder={placeholder}
            value={value}
            readOnly={readonly}
            data-type={dataType}
            name={name}
          />

          {unit && <span className='input__marker'>{unit}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className='w-100'>
      {label && (
        <div className='d-flex align-center justify-between'>
          <Paragrapgh
            className='mb-4 d-block'
            size={2}
            color='black'
            align='default'
            fontWeight={400}
          >
            {label}
          </Paragrapgh>
          {isOptional ? (
            <Paragrapgh
              className='mb-4 d-block'
              size={3}
              color='black-2'
              align='default'
              fontWeight={400}
            >
              Optional
            </Paragrapgh>
          ) : !!rightLabelButton ? (
            rightLabelButton
          ) : (
            <></>
          )}
        </div>
      )}
      <div className={unit ? 'd-flex' : 'pos-relative'}>
        {type === 'cardnumber' && (
          <NumberFormat
            format='#### #### #### ####'
            mask='_'
            onValueChange={values => onValChange && onValChange(values.value)}
            disabled={disabled}
            className={inputClasses}
            placeholder={placeholder}
            value={value}
            data-type={dataType}
            name={name}
          />
        )}
        {type === 'expiration' && (
          <NumberFormat
            format='##/##'
            mask={['M', 'M', 'Y', 'Y']}
            onValueChange={values =>
              onValChange && onValChange(values.formattedValue)
            }
            disabled={disabled}
            className={inputClasses}
            placeholder={placeholder}
            value={value}
            data-type={dataType}
            name={name}
          />
        )}
        {type === 'cvv' && (
          <NumberFormat
            format='###'
            onValueChange={values => onValChange && onValChange(values.value)}
            type='password'
            disabled={disabled}
            className={inputClasses}
            placeholder={placeholder}
            value={value}
            data-type={dataType}
            name={name}
          />
        )}
        {type === 'textarea' && (
          <textarea
            onChange={onChange}
            disabled={disabled}
            className={inputClasses}
            placeholder={placeholder}
            value={value}
            data-type={dataType}
            name={name}
          />
        )}
        {type !== 'textarea' &&
          type !== 'cardnumber' &&
          type !== 'expiration' &&
          type !== 'cvv' && (
            <input
              type={type}
              onChange={handleOnChange}
              onFocus={handleOnFocus}
              disabled={disabled}
              className={inputClasses}
              placeholder={placeholder}
              value={value}
              readOnly={readonly}
              data-type={dataType}
              name={name}
              {...(max !== undefined ? { max } : {})}
              {...(min !== undefined ? { min } : {})}
              onWheel={event => (event as any).target.blur()}
              list={list}
            />
          )}
        {type === 'date' && disabled && (
          <span className='input__icon'>
            <CalendarIcon color='#C0C0C0' />
          </span>
        )}
        {unit && <span className='input__marker'>{unit}</span>}
      </div>
    </div>
  );
};

export default Input;
